import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

/** Endpoints that should NOT have the Authorization header attached */
const PUBLIC_PATHS = [
  '/resource-access/login',
  '/resource-access/forgot-password',
  '/resource-access/reset-password',
];

/**
 * Functional HTTP interceptor that:
 * 1. Attaches Bearer token to all non-public API requests
 * 2. Handles 401 responses by logging out (token expired or invalid)
 *    EXCEPT during offline sync operations (to avoid losing queued actions)
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  const isPublic = PUBLIC_PATHS.some(path => req.url.includes(path));

  let request = req;
  if (!isPublic) {
    const token = authService.getToken();
    if (token) {
      request = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // Don't auto-logout on 401 if this is a sync operation (queued offline action)
      // The SyncService handles retry logic and will re-attempt after re-login.
      const isSyncRequest = req.headers.has('X-Offline-Sync');
      if (error.status === 401 && !isPublic && !isSyncRequest) {
        authService.logout();
      }
      return throwError(() => error);
    })
  );
};
